<script setup lang="ts">
import {
    type DialogPositionType,
    type OpenDialogOptions,
    type DialogBreakpoints,
    usePartialDialog,
    pickDialogProps,
} from '../dialogs';
import type { DivisibleInjectionKey } from '@precon-tools-suite-by-shared/vue-divisibles';
import type {
    DynamicDialogCloseOptions,
    DynamicDialogInstance,
} from 'primevue/dynamicdialogoptions';
import { computed, useSlots, type Component, type Slot } from 'vue';
import _ from 'lodash';

const props = withDefaults(
    defineProps<{
        partial: string | Component;
        fallbackPartial?: string | Component | null;
        // Dialog options
        partialProps?: Record<string, unknown>;
        partialSlots?: Record<string, unknown>;
        modal?: boolean;
        breakpoints?: DialogBreakpoints | undefined;
        closable?: boolean;
        maximizable?: boolean;
        dismissableMask?: boolean;
        closeOnEscape?: boolean;
        showHeader?: boolean;
        showFooter?: boolean;
        baseZIndex?: number | undefined;
        autoZIndex?: boolean;
        contentStyle?: string | Record<string, string> | undefined;
        contentClass?:
            | string
            | Array<string | Record<string, boolean>>
            | Record<string, boolean>
            | null;
        position?: DialogPositionType;
        draggable?: boolean;
        keepInViewPort?: boolean;
        minX?: number | undefined;
        minY?: number | undefined;
        dialogStyle?: string | Record<string, string> | undefined; // cast to 'style'
        dialogClass?:
            | string
            | Array<string | Record<string, boolean>>
            | Record<string, boolean>
            | undefined; // cast to 'class'
        data?: Record<string, unknown>;
        dialogProps?: Record<string, unknown>;
        receiverKey?: DivisibleInjectionKey;
        title?: string;
        subtitle?: string;
    }>(),
    {
        fallbackPartial: null,
        partialProps: () => ({}),
        partialSlots: () => ({}),
        breakpoints: undefined,
        modal: true,
        closable: true,
        maximizable: false,
        dismissableMask: true,
        closeOnEscape: true,
        showHeader: true,
        showFooter: true,
        baseZIndex: undefined,
        autoZIndex: true,
        contentStyle: undefined,
        contentClass: null,
        position: undefined,
        draggable: true,
        keepInViewPort: true,
        minX: undefined,
        minY: undefined,
        dialogStyle: undefined,
        dialogClass: undefined,
        data: () => ({}),
        dialogProps: () => ({}),
        receiverKey: undefined,
        title: undefined,
        subtitle: undefined,
    }
);

const emits = defineEmits<{
    (e: 'open', dialog: DynamicDialogInstance): void;
    (e: 'close', response: DynamicDialogCloseOptions | undefined): void;
}>();
const slots = useSlots();

const dialogOptions = computed(
    (): OpenDialogOptions => ({
        ...pickDialogProps(props),
        class: props.dialogClass,
        style: props.dialogStyle,
        partialSlots: {
            ..._.omit(slots, ['default']),
            default: slots.partialDefault as Slot,
            ...(props.partialSlots ?? {}),
        },
        onOpen: (dialog) => emits('open', dialog),
        onClose: (response) => emits('close', response),
    })
);

const dialog = usePartialDialog(
    () => props.partial,
    () => props.fallbackPartial
);

function openDialog() {
    dialog.open(dialogOptions.value);
}
</script>

<template>
    <Button v-bind="$attrs" @click.prevent="openDialog">
        <slot />
    </Button>
</template>
